import { createSelector } from "reselect"
import { get, chain, includes } from "lodash"
import { makeCurrencyFormatter, percentageFormatter } from "helpers/formatters/numbers"
import { getMissingDataFiles } from "selectors/missingDataFiles"
import { addCompositionTopXRow } from "helpers/analyze"
import { computeSummarySheetDownload, computeGroupSheetDownload } from "helpers/comparison"
import { getBetaDetails } from "modules/compare-composition/selectors/details"
import { getModels } from "modules/research-topic-root/selectors"
import { getCurrentLocation } from "modules/comparison/selectors"
import { getItems } from "./items"
import { getSelectedGroup } from "."

const EMPTY_OBJECT = {}

const getPositionSet0 = (state, props) => props.positionSets[0]
const getPositionSet1 = (state, props) => props.positionSets[1]
const getPositionSet2 = (state, props) => props.positionSets[2]
const getPositionSet3 = (state, props) => props.positionSets[3]
const getPositionSet4 = (state, props) => props.positionSets[4]
const getData0 = state => get(state, "global.data.compareTopicSummaryPositionSet0.data.model.simulation", EMPTY_OBJECT)
const getData1 = state => get(state, "global.data.compareTopicSummaryPositionSet1.data.model.simulation", EMPTY_OBJECT)
const getData2 = state => get(state, "global.data.compareTopicSummaryPositionSet2.data.model.simulation", EMPTY_OBJECT)
const getData3 = state => get(state, "global.data.compareTopicSummaryPositionSet3.data.model.simulation", EMPTY_OBJECT)
const getData4 = state => get(state, "global.data.compareTopicSummaryPositionSet4.data.model.simulation", EMPTY_OBJECT)
const getDataStatus0 = state => get(state, "global.data.compareTopicSummaryPositionSet0.status", EMPTY_OBJECT)
const getDataStatus1 = state => get(state, "global.data.compareTopicSummaryPositionSet1.status", EMPTY_OBJECT)
const getDataStatus2 = state => get(state, "global.data.compareTopicSummaryPositionSet2.status", EMPTY_OBJECT)
const getDataStatus3 = state => get(state, "global.data.compareTopicSummaryPositionSet3.status", EMPTY_OBJECT)
const getDataStatus4 = state => get(state, "global.data.compareTopicSummaryPositionSet4.status", EMPTY_OBJECT)
const getInstrumentDescriptors = (state, props) => props.instrumentDescriptors
const getModelId = (state, props) => props.modelId
export const getInstrumentPortfolio = (state, props) => props.instrumentPortfolio

function getDailyData(status, data, positionSet, selectedGroup) {
  const riskData = get(data, "risk.0")
  const compositionData = get(data, "composition.0")
  const betaData = get(data, "beta.0")
  const longShort = get(data, "composition.0.longShortComposition")
  if (!riskData || !compositionData || !longShort) return null

  const compositionBy = chain(compositionData.compositionBy)
    .map(item => ({ label: item.description, value: item.economicExposure }))
    .orderBy([item => Math.abs(item.value)], ["desc"])
    .value()

  // Add an extra row, at the start of the array, representing the TopX (X = Math.min(10, compositionBy.length)) total
  addCompositionTopXRow(compositionBy, selectedGroup.label)

  const long = chain(longShort).find({ id: "LONG" }).get("economicExposure", 0).value()
  const short = chain(longShort).find({ id: "SHORT" }).get("economicExposure", 0).value()
  const net = long - Math.abs(short)

  return {
    loading: status,
    totalRisk: riskData.total * 100,
    risk: {
      specific: riskData.attribution.summary.specific * 100,
      factors: riskData.attribution.summary.factors * 100,
    },
    compositionSummary: {
      gmv: get(compositionData, "gmv"),
      net,
      marketCapitalization: get(compositionData, "summaryStats.average.marketCapitalization"),
    },
    beta: {
      predicted: get(betaData, "predicted"),
      historical: get(betaData, "historical"),
    },
    groups: chain(compositionBy)
      .map(item => {
        const absValue = Math.abs(item.value)
        return {
          ...item,
          chartWidth: percentageFormatter(absValue < 100 ? absValue : 100, true),
        }
      })
      .value(),
  }
}

function getRiskContributors(data) {
  return get(data, "riskContributors")
}

const getDailyData0 = createSelector([getDataStatus0, getData0, getPositionSet0, getSelectedGroup], getDailyData)
const getDailyData1 = createSelector([getDataStatus1, getData1, getPositionSet1, getSelectedGroup], getDailyData)
const getDailyData2 = createSelector([getDataStatus2, getData2, getPositionSet2, getSelectedGroup], getDailyData)
const getDailyData3 = createSelector([getDataStatus3, getData3, getPositionSet3, getSelectedGroup], getDailyData)
const getDailyData4 = createSelector([getDataStatus4, getData4, getPositionSet4, getSelectedGroup], getDailyData)

const getRiskContributors0 = createSelector([getData0], getRiskContributors)
const getRiskContributors1 = createSelector([getData1], getRiskContributors)
const getRiskContributors2 = createSelector([getData2], getRiskContributors)
const getRiskContributors3 = createSelector([getData3], getRiskContributors)
const getRiskContributors4 = createSelector([getData4], getRiskContributors)

export const getSelectedModelName = createSelector(
  [getModelId, getModels],
  (modelId, models) => chain(models)
    .find(model => model.id === modelId)
    .get("name")
    .value(),
)

export const getDetailsData = createSelector(
  [getDailyData0, getDailyData1, getDailyData2, getDailyData3, getDailyData4],
  (data0, data1, data2, data3, data4) => ([data0, data1, data2, data3, data4]),
)

export const getDetailsItems = createSelector(
  [getDetailsData, getMissingDataFiles, getModelId, getSelectedModelName],
  (detailsData, missingDataFiles, modelId, modelName) => ({
    totalRisk: [{
      label: "Total Risk",
      fn: item => percentageFormatter(get(item, "totalRisk", 0), true),
    }],
    risk: [{
      label: "Specific",
      fn: item => percentageFormatter(get(item, "risk.specific", 0), true),
    }, {
      label: "Factors",
      fn: item => percentageFormatter(get(item, "risk.factors", 0), true),
    }],
    compositionSummary: [{
      label: "Gross Market Value",
      fn: (item, type) => !includes(["portfolio", "experiment", "inline"], type) ? "--" : makeCurrencyFormatter()(get(item, "compositionSummary.gmv", 0)),
    }, {
      label: "Market Capitalization",
      fn: item => makeCurrencyFormatter()(get(item, "compositionSummary.marketCapitalization", 0)),
    }, {
      label: "Net",
      fn: item => percentageFormatter(get(item, "compositionSummary.net", 0), true),
    }],
    ...getBetaDetails(missingDataFiles, modelId, modelName),
    groups: chain(detailsData)
      .map(item => item ? item.groups : null)
      .value(),
  }),
)

export const getSmartTradeRiskData = createSelector(
  [getRiskContributors0, getRiskContributors1, getRiskContributors2, getRiskContributors3, getRiskContributors4, getInstrumentDescriptors],
  (riskContributors0, riskContributors1, riskContributors2, riskContributors3, riskContributors4, descriptors) => {
    const { ticker, mic } = descriptors || {}

    const data = chain([
      riskContributors0,
      riskContributors1,
      riskContributors2,
      riskContributors3,
      riskContributors4,
    ]).map((riskContributors) => {
      if (!riskContributors) { return null }
      return {
        risk: get(riskContributors.find(({ id }) => id === `${ticker}.${mic}`), "attribution.summary.specific", null),
      }
    })
      .value()

    return {
      items: [{
        label: `${ticker}.${mic} specific`,
        fn: item => {
          const risk = get(item, "risk", 0)
          return risk === null ? "--" : percentageFormatter(risk)
        },
      }],
      data,
    }
  },
)

export const getTableDownloadData = createSelector(
  [getDetailsData, getDetailsItems, getSelectedGroup, getItems, getCurrentLocation],
  (detailsData, { totalRisk, risk, compositionSummary, beta, groups }, selectedGroup, items, currentLocation) => {
    if (currentLocation === "smart-trade") {
      return null
    }
    return {
      sheets: [
        computeSummarySheetDownload(detailsData, items, [totalRisk, risk, compositionSummary, beta, groups]),
        computeGroupSheetDownload(detailsData, items, selectedGroup),
      ],
    }
  },
)
